import React, { useState } from 'react';
import { Typography, Box, Stack, Tooltip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { CloudDownload } from '@mui/icons-material';
import { LessonProgress, VrLessonProgress } from 'models/LessonProgress';
import { LessonType } from 'models/LessonType';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import {
  StyledAccordion,
  StyledAccordionSummary,
  LessonNameBox,
  UnderlinedTypography,
  PercentageBox,
  StyledLinearProgress,
  PointsBox,
  PointsTypography,
  ReportIconButton
} from './LessonProgressAccordion.styles';
import { LessonProgressDetails } from 'components/views/myTrainings/LessonProgressDetails/LessonProgressDetails';
import { truncateString } from 'helpers/stringHelper';
import { LessonProgressStatus } from "models/LessonProgressStatus";
import { useDownloadMyNodeReport } from '@hooks/node/useDownloadMyNodeReport';

interface LessonProgressAccordionProps {
    index: number;
    lp: LessonProgress;
    historicalProgresses: LessonProgress[];
    expanded: number | null;
    handleChange: (panel: number) => (event: React.SyntheticEvent) => void;
}

export const LessonProgressAccordion = (props: LessonProgressAccordionProps) => {
  const [hasMounted, setHasMounted] = useState(false);
  const { index, lp, expanded, handleChange } = props;
  const theme = useTheme();
  const { t } = useTranslation();
  const { downloadReport, isReportLoading } = useDownloadMyNodeReport();

  React.useEffect(() => {
    if (expanded === index) {
      setHasMounted(true);
    }
  }, [expanded, index]);

  const version = lp.lessonVersion;
  const maxPoints = version.maxPoints || 1;
  const getScoredPoints = () =>
    !version ? 0 : (lp.pointsScored / maxPoints * 100).toFixed(0);

  const isVr = lp.type === LessonType.VR;
  const isGame = lp.type === LessonType.GAME;

  return (
    <StyledAccordion key={index} expanded={!lp?.createdAt ? false : expanded === index} onChange={handleChange(index)}>
        <StyledAccordionSummary
          aria-controls="panel1d-content"
          id="panel1d-header"
          expandIcon={<ExpandMoreIcon />}
        >
            <Stack direction={'row'} width={'100%'} display={'flex'} justifyContent={'space-between'} paddingLeft={theme.spacing(2)} alignItems={'center'}>
                <LessonNameBox>
                  <UnderlinedTypography>{truncateString(`${version.lessonName} (v${version.version})`, 25)}</UnderlinedTypography>
                </LessonNameBox>
                <PercentageBox sx={{ paddingLeft: theme.spacing(2), paddingRight: theme.spacing(2) }}>
                  <Typography>{getScoredPoints() + ' %'}</Typography>
                </PercentageBox>
                <StyledLinearProgress variant="determinate" value={lp.pointsScored / maxPoints * 100} />
                <Box display={'flex'} alignItems={'center'} justifyContent={'flex-end'}>
                    {isVr && (
                      <Tooltip title={t('myTrainings.report.getReport')}>
                        <span>
                          <ReportIconButton
                            aria-label="download-report"
                            onClick={(event) => {
                              event.stopPropagation();
                              downloadReport(lp as VrLessonProgress);
                            }}
                            disabled={isReportLoading}
                            data-testid="get-report-button"
                          >
                            <CloudDownload />
                          </ReportIconButton>
                        </span>
                      </Tooltip>
                    )}
                    <PointsBox>
                        <PointsTypography theme={theme} isCompleted={lp.lessonProgressStatus === LessonProgressStatus.Finished}>
                            {`${lp.pointsScored} / ${maxPoints}`}
                        </PointsTypography>
                    </PointsBox>
                </Box>
            </Stack>
        </StyledAccordionSummary>
        {hasMounted && (
          <LessonProgressDetails
            lp={lp}
            historicalProgresses={props.historicalProgresses}
            isVr={isVr}
            isGame={isGame}
          />
        )}
  </StyledAccordion>
  );
}
